import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { ArrowRight, Check, Copy, Loader2, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistForm({ onSuccess, compact = false }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [state, setState] = useState("idle");
  const [refCode, setRefCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [vipLoading, setVipLoading] = useState(false);

  const goVip = async () => {
    if (!form.email.includes("@")) {
      setState("invalid");
      return;
    }
    setVipLoading(true);
    try {
      if (state !== "done" && state !== "already") {
        const ref = new URLSearchParams(window.location.search).get("ref");
        const { data } = await axios.post(`${API}/waitlist`, { ...form, lang, ...(ref ? { ref } : {}) });
        if (data.status === "invalid_email") {
          setState("invalid");
          setVipLoading(false);
          return;
        }
        onSuccess?.(data.count);
        if (data.referral_code) setRefCode(data.referral_code);
        setState(data.status === "already_registered" ? "already" : "done");
      }
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: "vip_30_launch",
        origin_url: window.location.origin,
        email: form.email,
      });
      window.location.href = data.checkout_url;
    } catch {
      setVipLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/?ref=${refCode}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading" || state === "done") return;
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.includes("@")) {
      setState("invalid");
      return;
    }
    setState("loading");
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      const { data } = await axios.post(`${API}/waitlist`, { ...form, lang, ...(ref ? { ref } : {}) });
      if (data.status === "invalid_email") {
        setState("invalid");
        return;
      }
      onSuccess?.(data.count);
      if (data.referral_code) setRefCode(data.referral_code);
      setState(data.status === "already_registered" ? "already" : "done");
    } catch {
      setState("error");
    }
  };

  const inputCls =
    "w-full bg-[#111111] border border-white/10 rounded-sm px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#CCFF00]/60 transition-colors duration-300";

  return (
    <div id="waitlist" data-testid="waitlist-form" className="neon-trace bg-[#0A0A0A] p-5 sm:p-6">
      <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] mb-4">{t.hero.formLabel}</p>

      <AnimatePresence mode="wait">
        {state === "done" || state === "already" ? (
          <motion.div
            key="success"
            data-testid="waitlist-success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="py-5"
          >
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 shrink-0 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/40 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#CCFF00]" />
              </span>
              <p className="font-display font-bold text-white">
                {state === "done" ? t.hero.success : t.hero.already}
              </p>
            </div>
            {refCode && (
              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] mb-2">{t.hero.referralTitle}</p>
                <p className="text-xs text-zinc-400 mb-3">{t.hero.referralText}</p>
                <div className="flex items-center gap-2">
                  <code data-testid="referral-link" className="flex-1 truncate bg-[#111111] border border-white/10 rounded-sm px-3 py-2.5 text-xs text-zinc-300 font-mono2">
                    {`${window.location.origin}/?ref=${refCode}`}
                  </code>
                  <button
                    data-testid="referral-copy-button"
                    onClick={copyLink}
                    className="flex items-center gap-1.5 shrink-0 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-[10px] rounded-sm px-3 py-2.5 hover:bg-[#b3e600] transition-colors duration-300"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? t.hero.copied : t.hero.copy}
                  </button>
                </div>
                <div className="flex gap-2 mt-2">
                  <a
                    data-testid="share-whatsapp-button"
                    href={`https://wa.me/?text=${encodeURIComponent(t.hero.shareText)}%20${encodeURIComponent(`${window.location.origin}/?ref=${refCode}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 border border-white/10 rounded-sm px-3 py-2.5 text-[10px] font-mono2 tracking-widest uppercase text-zinc-300 hover:border-[#CCFF00]/50 hover:text-[#CCFF00] transition-colors duration-300"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </a>
                  <a
                    data-testid="share-x-button"
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(t.hero.shareText)}&url=${encodeURIComponent(`${window.location.origin}/?ref=${refCode}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 border border-white/10 rounded-sm px-3 py-2.5 text-[10px] font-mono2 tracking-widest uppercase text-zinc-300 hover:border-[#CCFF00]/50 hover:text-[#CCFF00] transition-colors duration-300"
                  >
                    <span className="font-black text-sm">𝕏</span>
                    {t.hero.share}
                  </a>
                </div>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] mb-2">{t.hero.vipTitle}</p>
                  <p className="text-xs text-zinc-400 mb-3">{t.hero.vipText}</p>
                  <button
                    data-testid="vip-checkout-button"
                    onClick={goVip}
                    disabled={vipLoading}
                    className="w-full flex items-center justify-center gap-2 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-xs rounded-sm px-4 py-3 hover:bg-[#b3e600] transition-colors duration-300 disabled:opacity-70"
                  >
                    {vipLoading ? (
                      t.hero.vipRedirect
                    ) : (
                      <>
                        {t.hero.vipButton}
                        <s className="opacity-50 font-normal normal-case tracking-normal">{t.hero.vipOldPrice}</s>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} exit={{ opacity: 0 }} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                data-testid="waitlist-input-first-name"
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder={t.hero.firstName}
                className={inputCls}
              />
              <input
                data-testid="waitlist-input-last-name"
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder={t.hero.lastName}
                className={inputCls}
              />
            </div>
            <input
              data-testid="waitlist-input-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t.hero.email}
              className={inputCls}
            />
            <motion.button
              data-testid="waitlist-submit-button"
              type="submit"
              disabled={state === "loading"}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center gap-2 bg-[#CCFF00] text-black font-bold uppercase tracking-widest text-sm rounded-sm px-6 py-3.5 hover:bg-[#b3e600] shadow-[inset_0_-2px_0_rgba(0,0,0,0.2)] transition-colors duration-300 disabled:opacity-70"
            >
              {state === "loading" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t.hero.submitting}
                </>
              ) : (
                <>
                  {t.hero.submit}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </motion.button>
            {(state === "error" || state === "invalid") && (
              <p data-testid="waitlist-error" className="text-xs text-red-400">
                {state === "invalid" ? t.hero.invalid : t.hero.error}
              </p>
            )}
            <div className="pt-3 mt-1 border-t border-white/10">
              <div className="flex items-center justify-between gap-3 mb-2.5">
                <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#CCFF00]">{t.hero.vipTitle}</p>
                <p className="shrink-0 font-mono2 text-[10px] tracking-widest text-zinc-500">
                  <s>{t.hero.vipOldPrice}</s>
                </p>
              </div>
              <button
                data-testid="vip-checkout-button-presignup"
                type="button"
                onClick={goVip}
                disabled={vipLoading}
                className="w-full flex items-center justify-center gap-2 border border-[#CCFF00]/40 text-[#CCFF00] font-bold uppercase tracking-widest text-xs rounded-sm px-4 py-3 hover:bg-[#CCFF00]/10 hover:border-[#CCFF00]/70 transition-colors duration-300 disabled:opacity-70"
              >
                {vipLoading ? t.hero.vipRedirect : t.hero.vipButton}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
